import * as Sentry from '@sentry/react';
import { all, call, put, select, takeLatest } from 'redux-saga/effects';
import {
    TOP_TAGS_REQUESTED,
    TOP_CONTRIBUTORS_REQUESTED,
    SEGMENTS_LIST_REQUESTED,
    SEGMENT_TAGS_REQUESTED,
    IMPACT_FILTER_REQUESTED,
    SPECIFIC_CHALLENGE_REQUESTED,
    CONNECTED_CHALLENGE_REQUESTED,
    CHALLENGE_CODES_REQUESTED,
    CHALLENGE_NOTIFICATIONS_REQUESTED,
    ADD_CHALLENGE_NOTIFICATION_REQUESTED,
    NOTIFICATION_LIMIT,
    ADD_CHALLENGE_CODE_REQUESTED,
    UPDATE_CHALLENGE_CODE_REQUESTED,
} from './constants';
import {
    topTagsSucceeded,
    topTagsFailed,
    topContributorsSucceeded,
    topContributorsFailed,
    segmentsListFailed,
    segmentsListSucceeded,
    segmentTagsFailed,
    segmentTagsSucceeded,
    impactFilterSucceeded,
    impactFilterFailed,
    challengeSucceeded,
    challenegeFailed,
    connectedChallengeSucceeded,
    connectedChallenegeFailed,
    challengeCodesSucceeded,
    challengeCodesFailed,
    setSelectedSegment,
    challengeNotificationsSucceeded,
    challengeNotificationsFailed,
    addChallengeNotificationSucceeded,
    addChallengeNotificationFailed,
    addChallengeCodeFailed,
    addChallengeCodeSucceeded,
    updateChallengeCodeSucceeded,
    updateChallengeCodeFailed,
} from './actions';
import {
    getChallengeTopTags,
    getChallengeTopContributors,
    getSegments,
    getSegmentTags,
    getChallengeById,
    getConnectedChallenges,
    getChallengeCodes,
    getMLTags,
    getChallengeNotifications,
    createChallengeNotification,
    createChallengecode,
    getLitters,
    updateChallengeCode,
} from '../../api';
import {
    getTaxonomy,
    getSelectDate,
    getChallengeId,
    getSegmentId,
    getSegmentTaxonomy,
    getSelectedFilter,
    getRequestedSegmentsLimit,
    getNotificationCurrentPage,
    getCodeId,
} from './selectors';
import { parseClientError } from '../../api/utils';
import { getUserData } from '../auth/selectors';
import { logException } from '../../logger';

// triggers when user `TOP_CONTRIBUTORS_REQUESTED` action is dispatched
function* fetchTopContributors() {
    let params = {};
    const date = yield select(getSelectDate);
    const challengeId = yield select(getChallengeId);
    const codeId = yield select(getCodeId);
    try {
        params = { challengeId, params: { ...date } };
        if (codeId && codeId != 'all') params.params.codeId = codeId;
        const topContributors = yield call(getChallengeTopContributors, params);
        yield put(topContributorsSucceeded(topContributors));
    } catch (e) {
        Sentry.captureException(e);
        const userData = yield select(getUserData);
        logException(e, { ...userData, challengeId });
        const error = parseClientError(e);
        const message = error && error.message ? error.message : error;
        yield put(topContributorsFailed(message));
    }
}

// triggers when user `TOP_TAGS_REQUESTED` action is dispatched
function* fetchTopTags() {
    let params = {};
    const challengeId = yield select(getChallengeId);
    const taxonomy = yield select(getTaxonomy);
    try {
        params = { challengeId, params: { taxonomy } };
        // fetch both user and ml tags
        const [userTags, mlTags] = yield all([
            yield call(getChallengeTopTags, params),
            yield call(getMLTags, { taxonomy, challengeId }),
        ]);

        // combine result to update in state
        const result = {
            totalTags:
                (userTags.total ? userTags.total : 0) +
                (mlTags.total ? mlTags.total : 0),
            totalMlTags: mlTags.total ? mlTags.total : 0,
            totalDistilledTags: userTags.total ? userTags.total : 0,
            topTags: {
                userTags: userTags.tags ? userTags.tags : [],
                mlTags: mlTags.tags ? mlTags.tags : [],
            },
        };
        yield put(topTagsSucceeded(result));
    } catch (e) {
        Sentry.captureException(e);
        const userData = yield select(getUserData);
        logException(e, { ...userData, challengeId, taxonomy });
        const error = parseClientError(e);
        const message = error && error.message ? error.message : error;
        yield put(topTagsFailed(message));
    }
}

function* fetchSegmentsList() {
    const challengeId = yield select(getChallengeId);
    const limit = yield select(getRequestedSegmentsLimit);
    try {
        const segments = yield call(getSegments, {
            challengeId,
            params: {
                limit,
            },
        });
        yield put(segmentsListSucceeded(segments));
    } catch (e) {
        Sentry.captureException(e);
        const userData = yield select(getUserData);
        logException(e, { ...userData, challengeId });
        const error = parseClientError(e);
        const message = error && error.message ? error.message : error;
        yield put(segmentsListFailed(message));
        // set selected segment as null since an error occured in fetching
        yield put(setSelectedSegment(null));
        yield put(segmentsListSucceeded([]));
    }
}

function* fetchSegmentTags() {
    let params = {};
    const segmentId = yield select(getSegmentId);
    const challengeId = yield select(getChallengeId);
    const taxonomy = yield select(getSegmentTaxonomy);

    try {
        params = { segmentId, params: { taxonomy } };
        // fetch both user and ml tags
        const [userTags, mlTags] = yield all([
            yield call(getSegmentTags, params),
            yield call(getMLTags, {
                taxonomy,
                challengeId,
                activityId: segmentId,
            }),
        ]);
        // combine result to update in state
        const result = {
            totalTags:
                (userTags.total ? userTags.total : 0) +
                (mlTags.total ? mlTags.total : 0),
            topTags: {
                userTags: userTags.tags ? userTags.tags : [],
                mlTags: mlTags.tags ? mlTags.tags : [],
            },
        };
        yield put(segmentTagsSucceeded(result));
    } catch (e) {
        Sentry.captureException(e);
        const userData = yield select(getUserData);
        logException(e, { ...userData, challengeId, segmentId, taxonomy });
        const error = parseClientError(e);
        const message = error && error.message ? error.message : error;
        yield put(segmentTagsFailed(message));
    }
}

// triggers when user `IMPACT_FILTER_REQUESTED` action is dispatched
function* filterLitters(action) {
    const taxonomy = yield select(getSelectedFilter);
    let params = {};
    const challengeId = yield select(getChallengeId);
    try {
        params.challengeId = challengeId;
        if (taxonomy) {
            params.params = { taxonomy };
        }
        let dimension = {
            ...(action.dimensions || {
                top: 90,
                bottom: -90,
                left: -180,
                right: 180,
            }),
        };

        if (dimension.top > 90) dimension.top = 90;
        if (dimension.bottom > -90) dimension.bottom = -90;
        if (dimension.left > -180) dimension.left = -180;
        if (dimension.right > 180) dimension.right = 180;
        const res = yield call(getLitters, {
            challengeIds: params.challengeId,
            zoom: action.zoom || 1,
            ...dimension,
        });
        yield put(impactFilterSucceeded(res.data));
    } catch (e) {
        Sentry.captureException(e);
        const userData = yield select(getUserData);
        logException(e, { ...userData, challengeId, taxonomy });
        const error = parseClientError(e);
        const message = error && error.message ? error.message : error;
        yield put(impactFilterFailed(message));
    }
}

// triggers when 'SPECIFIC_CHALLENGE_REQUESTED' action is dispatched
function* fetchChallenge() {
    const challengeId = yield select(getChallengeId);
    try {
        const challenge = yield call(getChallengeById, {
            challengeId,
            includeTopContributors: false,
        });
        yield put(challengeSucceeded(challenge));
    } catch (e) {
        Sentry.captureException(e);
        const userData = yield select(getUserData);
        logException(e, { ...userData, challengeId });
        const error = parseClientError(e);
        const message = error && error.message ? error.message : error;
        yield put(challenegeFailed(message));
    }
}

function* fetchConnectedChallenges() {
    const challengeId = yield select(getChallengeId);
    try {
        const challenges = yield call(getConnectedChallenges, { challengeId });
        yield put(connectedChallengeSucceeded(challenges));
    } catch (e) {
        Sentry.captureException(e);
        const userData = yield select(getUserData);
        logException(e, { ...userData, challengeId });
        const error = parseClientError(e);
        const message = error && error.message ? error.message : error;
        yield put(connectedChallenegeFailed(message));
    }
}

function* fetchChallengeCodes() {
    const challengeId = yield select(getChallengeId);

    try {
        const challengeCodes = yield call(getChallengeCodes, { challengeId });
        yield put(challengeCodesSucceeded(challengeCodes));
    } catch (e) {
        Sentry.captureException(e);
        const userData = yield select(getUserData);
        logException(e, { ...userData, challengeId });
        const error = parseClientError(e);
        const message = error && error.message ? error.message : error;
        yield put(challengeCodesFailed(message));
    }
}

function* fetchChallengeNotifications() {
    const challengeId = yield select(getChallengeId);

    try {
        const skip = yield select(getNotificationCurrentPage);
        const challengeNotifications = yield call(getChallengeNotifications, {
            challengeId,
            skip,
            limit: NOTIFICATION_LIMIT,
        });
        yield put(challengeNotificationsSucceeded(challengeNotifications));
    } catch (e) {
        Sentry.captureException(e);
        const userData = yield select(getUserData);
        logException(e, { ...userData, challengeId });
        const error = parseClientError(e);
        const message = error && error.message ? error.message : error;
        yield put(challengeNotificationsFailed(message));
    }
}

function* initChallengeNotification(action) {
    const challengeId = yield select(getChallengeId);

    try {
        const { title, message } = action;
        const addedNotification = yield call(createChallengeNotification, {
            challengeId,
            title,
            message,
        });
        yield put(addChallengeNotificationSucceeded({ ...addedNotification }));
    } catch (e) {
        Sentry.captureException(e);
        const userData = yield select(getUserData);
        logException(e, { ...userData, challengeId });
        const error = parseClientError(e);
        const message = error && error.message ? error.message : error;
        yield put(addChallengeNotificationFailed(message));
    }
}

function* initChallengeNewCode(action) {
    const challengeId = yield select(getChallengeId);

    try {
        const { code } = action;
        const addedNotification = yield call(createChallengecode, {
            challengeId,
            code,
        });
        yield put(addChallengeCodeSucceeded({ ...addedNotification }));
        yield call(fetchChallengeCodes);
    } catch (e) {
        Sentry.captureException(e);
        const userData = yield select(getUserData);
        logException(e, { ...userData, challengeId });
        const error = parseClientError(e);
        const message = error && error.message ? error.message : error;
        yield put(addChallengeCodeFailed(message));
    }
}

function* initChallengeUpdateCode(action) {
    const challengeId = yield select(getChallengeId);
    try {
        const { payload } = action;
        yield call(updateChallengeCode, {
            challengeId,
            payload,
        });
        yield put(updateChallengeCodeSucceeded({}));
        yield call(fetchChallengeCodes);
    } catch (e) {
        Sentry.captureException(e);
        const userData = yield select(getUserData);
        logException(e, { ...userData, challengeId });
        const error = parseClientError(e);
        const message =
            error && error.data ? error.data : error.message || error;

        yield put(updateChallengeCodeFailed(message));
    }
}

export default function*() {
    yield all([
        yield takeLatest(TOP_TAGS_REQUESTED, fetchTopTags),
        yield takeLatest(TOP_CONTRIBUTORS_REQUESTED, fetchTopContributors),
        yield takeLatest(SEGMENTS_LIST_REQUESTED, fetchSegmentsList),
        yield takeLatest(SEGMENT_TAGS_REQUESTED, fetchSegmentTags),
        yield takeLatest(IMPACT_FILTER_REQUESTED, filterLitters),
        yield takeLatest(SPECIFIC_CHALLENGE_REQUESTED, fetchChallenge),
        yield takeLatest(
            CONNECTED_CHALLENGE_REQUESTED,
            fetchConnectedChallenges,
        ),
        yield takeLatest(CHALLENGE_CODES_REQUESTED, fetchChallengeCodes),
        yield takeLatest(
            CHALLENGE_NOTIFICATIONS_REQUESTED,
            fetchChallengeNotifications,
        ),
        yield takeLatest(
            ADD_CHALLENGE_NOTIFICATION_REQUESTED,
            initChallengeNotification,
        ),
        yield takeLatest(ADD_CHALLENGE_CODE_REQUESTED, initChallengeNewCode),
        yield takeLatest(
            UPDATE_CHALLENGE_CODE_REQUESTED,
            initChallengeUpdateCode,
        ),
    ]);
}
